import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, Clapperboard, Mail, MessageSquareText, TrendingUp, BadgeDollarSign } from "lucide-react";
import stats from "./content/site-stats.json";
import { DRAFT_SERVICES, SERVICE_ORDER } from "./content/draft-services";
import { PrimaryButton, SecondaryButton, CTABand } from "../components/Buttons";
import { useMeta } from "../hooks/useMeta";
import { draftPath } from "./DraftLayout";
import { WinsTeaser } from "./WinsTeaser";
import { CountUp } from "./CountUp";
import { Compare, Faq } from "./Compare";
import { Shell, H2, Reveal, CreativeWall, StatRow, MiniCaseCard, LogoBar, VideoTestimonials, WhoFor } from "./parts";

const FEATURED = ["clean-eez", "good-riddance", "jamie-anne"];

function Hero() {
  return (
    <section data-testid="draft-hero" className="grain overflow-hidden bg-navy">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 pb-10 pt-16 md:grid-cols-[1.1fr_0.9fr] md:pb-0 md:pt-20">
        <div className="relative z-20">
          <h1 className="text-4xl font-bold leading-[1.05] tracking-[-0.035em] text-white sm:text-5xl lg:text-[58px]">
            We run our own brands. <span className="text-pink">We'll run yours like it's ours.</span>
          </h1>
          <div data-testid="hero-track-record" className="mt-8 grid max-w-xl grid-cols-3 gap-6">
            {[["15", "years building and scaling our own brands"], ["10", "years teaching others how to do the same"], ["5", "years doing it for agency clients"]].map(([n, l]) => (
              <div key={n} className="border-l border-white/20 pl-4">
                <div className="text-4xl font-black leading-none tracking-[-0.04em] text-white md:text-5xl">{n}</div>
                <div className="mt-2 text-sm leading-snug text-white/70">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg">
            So we don't just manage your marketing. We treat your business like it's our own and optimize for what matters most: profit.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <PrimaryButton testId="draft-hero-work-with-us">Work with us</PrimaryButton>
            <Link to={draftPath("/case-studies")} data-testid="draft-hero-see-results" className="inline-block rounded-full border-2 border-white px-8 py-4 text-base font-bold text-white transition-colors hover:bg-white hover:text-navy">
              See the results
            </Link>
          </div>
        </div>
        <div className="relative hidden md:block">
          <div data-testid="hero-badge-brands" className="absolute -left-12 top-14 z-20 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_16px_40px_-8px_rgba(0,0,0,0.5)]">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#DDF3FF] text-link"><TrendingUp size={20} /></span>
            <div><div className="text-lg font-black leading-none tracking-[-0.03em] text-navy">10,000 brands</div><div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/60">scaled</div></div>
          </div>
          <div data-testid="hero-badge-revenue" className="absolute -right-4 bottom-20 z-20 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-[0_16px_40px_-8px_rgba(0,0,0,0.5)]">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFE0EB] text-pink"><BadgeDollarSign size={20} /></span>
            <div><div className="text-lg font-black leading-none tracking-[-0.03em] text-navy">$200 million</div><div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/60">for our own brands</div></div>
          </div>
          <CreativeWall />
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 pb-6 pt-4 md:pb-10">
        <StatRow items={stats.proof} dark testId="draft-proof-stats" />
      </div>
    </section>
  );
}

function WhyOperators() {
  return (
    <Shell testId="why-operators">
      <div className="grid gap-12 md:grid-cols-2 md:items-start">
        <div>
          <H2>Our first-hand experience sets us apart.</H2>
          <div data-testid="why-operators-copy" className="mt-6 space-y-5 text-base leading-relaxed text-navy/80 md:text-lg">
            <p>We've built our own brands from scratch, we still run them, and we still put our own money into ads every day.</p>
            <p className="font-bold text-navy">That changes how we work on yours.</p>
            <p>We think like owners. We care about the full P&L. We challenge decisions, look beyond surface-level metrics, and work closely with your team to build growth that lasts.</p>
            <p className="font-bold text-navy">We've sat on your side of the table. We still do.</p>
          </div>
          <div className="mt-10">
            <SecondaryButton href={draftPath("/how-we-work")} testId="draft-how-we-work-btn">How we work</SecondaryButton>
          </div>
        </div>
        <Reveal>
          <div data-testid="team-visual" className="relative mt-10 md:mt-0">
                        <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-b from-[#0F0873] to-navy pt-8 shadow-[0_24px_50px_rgba(7,0,93,0.35)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(242,0,94,0.16),transparent_65%)]" aria-hidden="true" />
              <img src={stats.images.team} alt="Ezra Firestone, Molly Pittman, John Grimshaw and Pepijn Hufen" className="relative mx-auto w-[86%] drop-shadow-[0_24px_40px_rgba(0,0,0,0.45)]" />
              <div data-testid="team-visual-stats" className="relative grid grid-cols-3 divide-x divide-white/10 border-t border-white/10 bg-navy/80 backdrop-blur-md">
                {[["$200M+", "own-brand revenue"], ["440M+", "emails sent"], ["15 yrs", "running our own brands"]].map(([v, l]) => (
                  <div key={l} className="px-4 py-4 text-center">
                    <div className="text-xl font-extrabold text-white md:text-2xl">{v}</div>
                    <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-white/60">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </Shell>
  );
}

function SameSpend() {
  return (
    <Shell tone="fog" testId="same-spend">
      <H2>{stats.sameSpendHeading}</H2>
      <p className="mt-3 text-base text-navy/70 md:text-lg">{stats.sameSpendSub}</p>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {stats.sameSpend.map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.08}>
            <Link to={`${draftPath("/case-studies")}/${s.slug}`} data-testid={`same-spend-${s.slug}`} className="group block h-full rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-8">
              {s.counter ? (
                <>
                  <div className="text-4xl font-extrabold leading-tight text-navy md:text-5xl"><CountUp counter={s.counter} testId={`same-spend-count-${s.slug}`} /></div>
                  <div className="mt-2 text-xs font-medium uppercase tracking-widest text-navy/60">{s.label}<span className="normal-case tracking-normal text-navy/50">, {s.counter.fromLabel}</span></div>
                </>
              ) : (
                <>
                  <div className="text-3xl font-extrabold leading-tight text-navy md:text-4xl">{s.value}</div>
                  <div className="mt-2 text-xs font-medium uppercase tracking-widest text-navy/60">{s.label}</div>
                </>
              )}
              <p className="mt-4 text-sm leading-relaxed text-navy/80">{s.sub}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-link transition-all group-hover:gap-3">See the full case study <ArrowRight size={16} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </Shell>
  );
}

function Featured() {
  return (
    <Shell testId="featured-results">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <H2>Proof, in their numbers.</H2>
        <SecondaryButton href={draftPath("/case-studies")} testId="draft-all-case-studies-btn">All case studies</SecondaryButton>
      </div>
      <div className="mt-10 grid gap-6 lg:grid-cols-[1.25fr_1fr]">
        <Reveal className="h-full"><WinsTeaser exclude={FEATURED} /></Reveal>
        <div className="grid gap-6">
          {FEATURED.map((slug, i) => (
            <Reveal key={slug} delay={i * 0.08}>
              <MiniCaseCard slug={slug} />
            </Reveal>
          ))}
        </div>
      </div>
    </Shell>
  );
}

const SERVICE_ICONS = { "paid-traffic": Megaphone, "ad-creative": Clapperboard, retention: Mail, aeo: MessageSquareText };
const SERVICE_ART = { "paid-traffic": "/assets/legacy/ill-ads-white.png", "ad-creative": "/assets/legacy/ill-creative-white.png", retention: "/assets/legacy/ill-email-white.png", aeo: "/assets/legacy/ill-aeo-white.png" };

function Services() {
  return (
    <Shell tone="navy" testId="services-section">
      <div className="max-w-2xl">
        <H2 light>Our services.</H2>
        <p className="mt-4 text-base text-white/70 md:text-lg">Four services, one team, one number we manage to: profit.</p>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-12">
        {SERVICE_ORDER.map((k, i) => {
          const s = DRAFT_SERVICES[k];
          const Icon = SERVICE_ICONS[k];
          return (
            <Reveal key={k} delay={i * 0.07} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-6"}>
              <Link
                to={draftPath(`/${k}`)}
                data-testid={`service-card-${k}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D056E] p-8 text-white transition-[transform,border-color,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-pink/50 hover:bg-[#120780] hover:shadow-[0_20px_48px_rgba(0,0,0,0.35)] md:p-10"
              >
                <img src={SERVICE_ART[k]} alt="" aria-hidden="true" className={`pointer-events-none absolute -right-6 -top-6 w-44 opacity-[0.16] transition-[opacity,transform] duration-500 group-hover:opacity-30 group-hover:-translate-y-1 ${i === 0 ? "md:w-60" : ""}`} />
                <div className="relative flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-pink"><Icon size={24} /></span>
                </div>
                <h3 className="mt-6 text-3xl font-bold leading-tight tracking-[-0.02em] text-white md:text-4xl">{s.name}</h3>
                <p className="mt-4 mb-8 text-base leading-relaxed text-white/75">{s.cardLine}</p>
                <div className="mt-auto border-t border-mist pt-6">
                  <div className="text-4xl font-black leading-none tracking-[-0.03em] text-white md:text-5xl">{s.proofStat.value}</div>
                  <div className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/55">{s.proofStat.label}. {s.proofStat.client}</div>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-base font-bold text-white transition-all group-hover:gap-3 group-hover:text-pink">
                  Explore {s.name} <ArrowRight size={18} />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </Shell>
  );
}

function FromMolly() {
  const m = stats.molly;
  return (
    <Shell testId="from-molly">
      <div className="grid gap-12 md:grid-cols-[0.55fr_1.45fr] md:items-center">
        <Reveal>
          <div className="relative mx-auto max-w-[320px]">
            <img src={m.photo} alt="Molly Pittman, CEO of Smart Marketer" className="w-full rounded-3xl shadow-[0_24px_50px_-12px_rgba(7,0,93,0.35)]" />
            <div className="absolute -bottom-4 left-6 rounded-full bg-navy px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-lg">Molly Pittman, CEO</div>
          </div>
        </Reveal>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">A note from Molly</div>
          <H2 className="mt-3">{m.heading}</H2>
          {(() => {
            const idx = m.body.indexOf(". ", m.body.indexOf(". ") + 2);
            const pull = idx > 0 ? m.body.slice(0, idx + 1) : m.body;
            const rest = idx > 0 ? m.body.slice(idx + 2) : "";
            return (
              <>
                <p className="mt-6 font-serif text-[24px] italic leading-[1.4] tracking-[-0.01em] text-navy md:text-[30px]">{pull}</p>
                {rest && <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/75 md:text-lg">{rest}</p>}
              </>
            );
          })()}
          <div className="mt-10">
            <SecondaryButton href={draftPath("/about")} testId="draft-meet-team-btn">Meet the team</SecondaryButton>
          </div>
        </div>
      </div>
    </Shell>
  );
}

export default function DraftHome() {
  useMeta({ title: "Smart Marketer Agency. We run our own brands. We'll run yours like it's ours.", description: "Paid traffic, ad creative, retention and AEO for DTC brands, managed to profit by operators who run their own." });
  return (
    <main data-testid="draft-home">
      <Hero />
      <WhyOperators />
      <SameSpend />
      <Featured />
      <Services />
      <LogoBar />
      <Compare />
      <Faq />
      <FromMolly />
      <VideoTestimonials />
      <WhoFor />
      <section data-testid="team-band" className="relative h-[320px] overflow-hidden md:h-[420px]">
        <img src="/assets/legacy/team-duotone.webp" alt="The Smart Marketer Agency team" className="h-full w-full object-cover object-[50%_30%]" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-6xl px-5 pb-10">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">The team</div>
          <div className="mt-2 max-w-xl text-2xl font-bold leading-tight tracking-[-0.02em] text-white md:text-3xl">Operators who still run their own brands, on your account every day.</div>
        </div>
      </section>
      <CTABand title="See if we're a fit." sub="Work with our Agency to get a proven approach tailored to your needs and executed by our results-driven team." />
    </main>
  );
}
